import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, TrendingUp, Users, Award } from 'lucide-react';

interface SocialProofSectionProps {
  companyName?: string;
}

export const SocialProofSection: React.FC<SocialProofSectionProps> = ({ 
  companyName = "votre entreprise" 
}) => {
  const testimonials = [
    {
      name: "Marc Dubois",
      company: "TechSolutions SA",
      role: "Directeur Marketing",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      content: "En 3 mois, notre trafic a explosé de +340%. L'équipe Smart Impact a transformé notre présence digitale. ROI exceptionnel !",
      results: "+340% trafic",
      rating: 5
    },
    {
      name: "Sophie Martin",
      company: "InnovateCorp",
      role: "CEO",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      content: "Professionnalisme remarquable. Nos conversions ont doublé et notre positionnement Google s'est considérablement amélioré.",
      results: "+180% conversions",
      rating: 5
    },
    {
      name: "Pierre Leroy",
      company: "SwissConsulting",
      role: "Fondateur",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      content: "Résultats au-delà de nos attentes. Site 3x plus rapide, leads qualifiés en hausse constante. Je recommande vivement !",
      results: "+290% vitesse",
      rating: 5
    }
  ];

  const beforeAfterStats = [
    {
      label: "Trafic Organique",
      before: "2,400",
      after: "9,280",
      increase: "+287%",
      icon: TrendingUp
    },
    {
      label: "Leads Qualifiés",
      before: "12",
      after: "31",
      increase: "+158%",
      icon: Users
    },
    {
      label: "Vitesse Site",
      before: "4.2s",
      after: "1.1s",
      increase: "+74%",
      icon: Award
    }
  ];

  const trustIndicators = [
    { number: "300+", label: "Clients satisfaits" },
    { number: "95%", label: "Taux de satisfaction" },
    { number: "4.9/5", label: "Note moyenne" },
    { number: "90j", label: "Résultats garantis" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22%23f97316%22%20fill-opacity%3D%220.03%22%20fill-rule%3D%22evenodd%22%3E%3Cpath%20d%3D%22M20%2020c0%2011.046-8.954%2020-20%2020s-20-8.954-20-20%208.954-20%2020-20%2020%208.954%2020%2020zm-20-18c-9.941%200-18%208.059-18%2018s8.059%2018%2018%2018%2018-8.059%2018-18-8.059-18-18-18z%22/%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 border border-green-200 text-green-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Preuves de Résultats
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                +300% de trafic
              </span>{' '}
              en moyenne pour nos clients
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Découvrez comment nous avons transformé la présence digitale de dizaines d'entreprises suisses 
              comme {companyName}.
            </p>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          >
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {indicator.number}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {indicator.label}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Before/After Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 mb-16 relative overflow-hidden"
          >
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-orange-400/5 rounded-full blur-2xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-12">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Avant / Après : Résultats réels d'un client
                </h3>
                <p className="text-gray-300 text-lg">
                  TechSolutions SA - Transformation en 90 jours
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {beforeAfterStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/30">
                      <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <stat.icon className="w-6 h-6 text-orange-400" />
                      </div>
                      
                      <h4 className="text-white font-semibold mb-4">{stat.label}</h4>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Avant :</span>
                          <span className="text-red-400 font-mono">{stat.before}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Après :</span>
                          <span className="text-green-400 font-mono">{stat.after}</span>
                        </div>
                      </div>
                      
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                        <span className="text-green-400 font-bold text-lg">
                          {stat.increase}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full relative">
                  
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <Quote className="w-4 h-4 text-white" />
                  </div>

                  {/* Rating */}
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Content */}
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>

                  {/* Results Badge */}
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-semibold mb-6">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {testimonial.results}
                  </div>

                  {/* Author */}
                  <div className="flex items-center">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {testimonial.role}
                      </div>
                      <div className="text-sm text-orange-600 font-medium">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA Teaser */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-orange-100 border border-orange-200 text-orange-700 font-semibold">
              <Award className="w-5 h-5 mr-2" />
              Prêt à obtenir les mêmes résultats ?
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
